import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";

const SearchableSelect = ({
  options = [],
  value,
  name,
  onChange,
  placeholder = "Select Option",
  className = "",
  disabled = false,
  showSearch = true,
  isValueVisible = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const filteredOptions = showSearch
    ? options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    ) : options;

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option.value, id: option.id } });
    setIsOpen(false);
    setSearchTerm("");
  };

  useEffect(() => {
    if (isOpen) {
      const selectedIdx = filteredOptions.findIndex((opt) => opt.value === value);
      setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [isOpen, filteredOptions, value]);

  return (
    <div
      className={`position-relative ${className}`}
      ref={dropdownRef}
      style={{ width: "100%" }}
    >
      <div
        className={`form-control radius-8 d-flex align-items-center justify-content-between cursor-pointer searchable-select-trigger ${disabled ? "searchable-select-trigger--disabled" : ""} ${error ? "border-danger is-invalid" : ""}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ height: "40px", overflow: "hidden", whiteSpace: "nowrap" }}
        onKeyDown={(e) => {
          if (!isOpen) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
          }

          if (e.key === "ArrowUp") {
            console.log("ArrowUp", highlightedIndex);
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
          }

          if (e.key === "Enter" && highlightedIndex >= 0) {
            console.log("Enter", highlightedIndex);
            e.preventDefault();
            handleSelect(filteredOptions[highlightedIndex]);
          }
        }}
        tabIndex={0} // IMPORTANT (makes div focusable)
      >
        <span
          className={`text-truncate mr-2 ${selectedOption ? "searchable-select-value" : "searchable-select-placeholder"}`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Icon
          icon={isOpen ? "lucide:chevron-up" : "lucide:chevron-down"}
          className="searchable-select-chevron flex-shrink-0"
        />
      </div>

      {isOpen && (
        <div
          className="position-absolute w-100 radius-8 shadow-lg mt-1 overflow-hidden searchable-select-menu"
          style={{ zIndex: 9999, top: "100%" }}
        >
          {showSearch && (
            <div className="p-2 searchable-select-search">
              <div className="searchable-select-search-wrap">
                <span className="searchable-select-search-icon">
                  <Icon icon="ion:search-outline" />
                </span>
                <input
                  type="text"
                  className="searchable-select-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
          )}
          <div
            className=""
            style={{ maxHeight: "250px", overflowY: "auto" }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={`px-12 py-8 cursor-pointer searchable-select-option ${value === option.value ? "searchable-select-option--active fw-semibold" : ""} ${index === highlightedIndex
                    ? "searchable-select-option--hover"
                    : ""
                    }`}
                  onClick={() => {
                    handleSelect(option);
                  }}
                  style={{ fontSize: "13px" }}
                >
                  {option.label}
                  {isValueVisible && (
                    <span className="text-secondary-light ms-2">
                      ({option.value})
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div
                className="px-12 py-16 text-center searchable-select-empty italic"
                style={{ fontSize: "12px" }}
              >
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
      {error && (
        <div className="text-danger mt-1" style={{ fontSize: "12px", display: "block" }}>
          {error}
        </div>
      )}

      <style>{`
        .cursor-pointer { cursor: pointer; }
        .w-150-px { width: 150px !important; }
        .w-200-px { width: 200px !important; }
        .text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        /* Theme-aware styles (works with html[data-theme="dark"]) */
        .searchable-select-trigger {
          padding: 0.45rem 0.75rem;
          gap: 0.5rem;
        }

        .searchable-select-trigger--disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .searchable-select-placeholder {
          color: var(--text-secondary-light);
        }

        .searchable-select-value {
          color: var(--text-primary-light);
        }

        .searchable-select-chevron {
          color: var(--text-secondary-light);
          font-size: 14px;
        }

        .searchable-select-menu {
          background-color: #fff !important;
          border: 1px solid var(--border-color);
          isolation: isolate;
        }

        html[data-theme="dark"] .searchable-select-menu {
          background-color: #1e293b !important;
        }

        .searchable-select-search {
          border-bottom: 1px solid var(--border-color);
          background-color: inherit;
        }

        .searchable-select-search-wrap {
          height: 34px;
          display: flex;
          align-items: center;
          border: 1px solid var(--border-color);
          border-radius: 8px;
          background-color: transparent;
          padding: 0 8px;
          gap: 6px;
        }

        .searchable-select-search-icon {
          color: var(--text-secondary-light);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .searchable-select-search-input {
          flex: 1;
          min-width: 0;
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
          background: transparent !important;
          color: var(--text-primary-light) !important;
          font-size: 12px !important;
          line-height: 1.2;
          padding: 0 !important;
        }

        .searchable-select-search-input::placeholder {
          color: var(--text-secondary-light) !important;
          font-size: 12px !important;
        }

        .searchable-select-option {
          background-color: inherit;
          color: var(--text-secondary-light);
        }

        .searchable-select-option:hover {
          background-color: rgba(37, 99, 235, 0.08);
          color: var(--text-primary-light);
        }

        .searchable-select-option--active {
          background-color: rgba(37, 99, 235, 0.14);
          color: #2563eb;
        }

        html[data-theme="dark"] .searchable-select-option--active {
          color: #93c5fd;
        }

        .searchable-select-empty {
          color: var(--text-secondary-light);
        }

        .searchable-select-option--hover {
          background-color: var(--primary-50);
          color: var(--primary-500) !important;
        }
      `}</style>
    </div>
  );
};

export default SearchableSelect;
